/** Shared, source-backed contracts for the Pages API and Queue workers. */

export const SCHEMA_VERSION = "1.0" as const;
export const SOURCE_ENVIRONMENT = "QA SOURCE" as const;

export type Status =
  | "HEALTHY"
  | "DEGRADED"
  | "CRITICAL"
  | "STALE"
  | "PARTIAL"
  | "NOT_CONFIGURED"
  | "DOCUMENTATION_GAP";

export type Grain = "5m" | "15m" | "hour" | "day";
export type Region = "IO" | "EU" | "SG" | "OTHER";

export const CATEGORIES = [
  { key: "trends", label: "Trends", description: "Solutions, purchased, activation, usage, segmentation, month-on-month and health" },
  { key: "accounts", label: "Accounts", description: "Lifecycle, customer cohorts, partner apps, IoT and Meraki adoption" },
  { key: "solutions", label: "Solutions", description: "Solution adoption and health" },
  { key: "support", label: "Support", description: "Open tickets and response posture" },
  { key: "renewals", label: "Renewals", description: "Upcoming AP and solution renewals" },
  { key: "sales-motion", label: "Sales Motion", description: "Sales hierarchy and motion coverage" },
  { key: "stats-trends", label: "Stats Trends", description: "Business statistics over time" },
  { key: "sensor-connect", label: "Sensor Connect", description: "Sensor Connect KPI posture" },
  { key: "adoption", label: "Adoption", description: "AP Auto Locate adoption" },
] as const;

export type CategoryKey = (typeof CATEGORIES)[number]["key"];

export type MetricDomain =
  | "trends-solutions"
  | "trends-purchased"
  | "trends-activation"
  | "trends-usage-onboarded"
  | "trends-ap-segmentation"
  | "trends-month-on-month"
  | "trends-health"
  | "accounts-lifecycle"
  | "accounts-paid"
  | "accounts-trials"
  | "accounts-internal"
  | "accounts-showcase-24"
  | "accounts-lighthouse"
  | "accounts-top-100"
  | "accounts-qbo"
  | "accounts-history"
  | "accounts-rich-map-area"
  | "accounts-partner-apps"
  | "accounts-ap-models"
  | "accounts-iot-sensors"
  | "accounts-iot-services"
  | "accounts-kontakt-activated"
  | "accounts-kontakt-upsell"
  | "accounts-meraki-tags"
  | "accounts-connectors-controllers"
  | "accounts-meraki-spaces"
  | "accounts-meraki-org"
  | "accounts-meraki-ble"
  | "solutions"
  | "support-open-tickets"
  | "renewals"
  | "sales-motion"
  | "stats-trends"
  | "sensor-connect"
  | "adoption-ap-auto-locate";

export interface RouteDefinition {
  readonly domain: MetricDomain;
  readonly path: string;
  readonly label: string;
  readonly category: CategoryKey;
  readonly unit: "count" | "rate" | "duration" | "currency" | "status" | "table";
  readonly grain: readonly Grain[];
  readonly dimensions: readonly string[];
  readonly sourceKey: string;
  readonly documentationGap?: boolean;
}

const route = (
  domain: MetricDomain,
  path: string,
  label: string,
  category: CategoryKey,
  sourceKey: string,
  unit: RouteDefinition["unit"] = "table",
  dimensions: readonly string[] = ["region", "solution", "accountType"],
  documentationGap = false,
): RouteDefinition => ({
  domain,
  path,
  label,
  category,
  unit,
  grain: ["15m", "hour", "day"],
  dimensions,
  sourceKey,
  documentationGap,
});

export const API_ROUTE_DEFINITIONS: readonly RouteDefinition[] = [
  route("trends-solutions", "/api/v1/trends/solutions", "Solutions", "trends", "slicksolution.historySolutionData", "count"),
  route("trends-purchased", "/api/v1/trends/purchased", "Purchased", "trends", "slicksolution.purchaseSplit", "count"),
  route("trends-activation", "/api/v1/trends/activation", "Activation", "trends", "slicksolution.timeToActivate", "duration"),
  route("trends-usage-onboarded", "/api/v1/trends/usage-onboarded", "Usage / APs Onboarded (ITD)", "trends", "slicksolution.apsPurchasedVsLive", "count"),
  route("trends-ap-segmentation", "/api/v1/trends/ap-segmentation", "APs Segmentation", "trends", "slicksolution.purchasedSegmentation", "count"),
  route("trends-month-on-month", "/api/v1/trends/month-on-month", "Month on Month", "trends", "slicksolution.lastMonthSolutionAdoption", "rate"),
  route("trends-health", "/api/v1/trends/health", "Health", "trends", "slickUsage.usageTrends", "status", ["region", "source", "severity"]),

  route("accounts-lifecycle", "/api/v1/accounts/lifecycle", "Lifecycle", "accounts", "slickread.lifecycle", "table", ["region", "accountType", "license", "solution"]),
  route("accounts-paid", "/api/v1/accounts/paid", "Paid", "accounts", "slickhome.accountJourney.paid", "count"),
  route("accounts-trials", "/api/v1/accounts/trials", "Trials", "accounts", "slickhome.accountJourney.trials", "count"),
  route("accounts-internal", "/api/v1/accounts/internal", "Internal", "accounts", "slickhome.accountJourney.internal", "count"),
  route("accounts-showcase-24", "/api/v1/accounts/showcase-24", "Showcase 24", "accounts", "slicksolution.goldCustomerList", "table"),
  route("accounts-lighthouse", "/api/v1/accounts/lighthouse", "Lighthouse Customer Program", "accounts", "slicksolution.getwellCustomers", "table"),
  route("accounts-top-100", "/api/v1/accounts/top-100", "Top 100 Customers", "accounts", "slicksolution.topCustomers", "table"),
  route("accounts-qbo", "/api/v1/accounts/qbo", "QBO Reports", "accounts", "slickQboHis.qboReports", "table"),
  route("accounts-history", "/api/v1/accounts/history", "History", "accounts", "slickread.lifecycleHistory", "table", ["region", "accountType", "changeType"]),
  route("accounts-rich-map-area", "/api/v1/accounts/rich-map-area", "Rich Map Area (Beta)", "accounts", "slickread.mapAreaSummary", "count", ["region", "mapArea"]),
  route("accounts-partner-apps", "/api/v1/accounts/partner-apps", "Partner Apps", "accounts", "slickTenantdetails.partnerApps", "count", ["region", "partner", "solution"]),
  route("accounts-ap-models", "/api/v1/accounts/ap-models", "AP Models", "accounts", "slickread.apModelDetails", "count", ["region", "apModel"]),
  route("accounts-iot-sensors", "/api/v1/accounts/iot-sensors", "IoT Sensors", "accounts", "slickread.iotTagsSummary", "count", ["region", "sensorType"]),
  route("accounts-iot-services", "/api/v1/accounts/iot-services", "IoT Services Reports", "accounts", "slickread.iotHistory", "count", ["region", "service"]),
  route("accounts-kontakt-activated", "/api/v1/accounts/kontakt-activated", "Kontakt Profile Activated", "accounts", "slickread.kontaktProfileStatus", "count", ["region", "license"]),
  route("accounts-kontakt-upsell", "/api/v1/accounts/kontakt-upsell", "Kontakt Profile Upsell Accounts", "accounts", "slickKontaktProfile.kontaktTrials", "count", ["region", "license", "activationState"]),
  route("accounts-meraki-tags", "/api/v1/accounts/meraki-tags", "Meraki Tags", "accounts", "slickread.merakiTags", "count", ["region", "tagType"]),
  route("accounts-connectors-controllers", "/api/v1/accounts/connectors-controllers", "Connectors & Controllers", "accounts", "slicksolution.connectorControllerDetails", "count", ["region", "connector", "controller"]),
  route("accounts-meraki-spaces", "/api/v1/accounts/meraki-spaces", "Meraki Spaces Integration", "accounts", "slickread.mkIntegration", "count", ["region", "integrationState"]),
  route("accounts-meraki-org", "/api/v1/accounts/meraki-org", "Meraki Integration By Org", "accounts", "slicksolution.merakiOrgHistory", "count", ["region", "orgState"]),
  route("accounts-meraki-ble", "/api/v1/accounts/meraki-ble", "Meraki BLE Data In", "accounts", "slickMerakiIoT.merakiIoTAccounts", "count", ["region", "sensorType"]),

  route("solutions", "/api/v1/solutions", "Solutions", "solutions", "slicksolution.solutionAdoption", "count"),
  route("support-open-tickets", "/api/v1/support/open-tickets", "Open Tickets", "support", "slickread.freshdeskOpenTickets", "count", ["region", "priority", "ageBucket"]),
  route("renewals", "/api/v1/renewals", "Renewals", "renewals", "slickread.apPurchaseRenewals", "count", ["region", "renewalWindow", "solution"]),
  route("sales-motion", "/api/v1/sales-motion", "Sales Motion", "sales-motion", "salesMotion.httpsRelay", "table", ["region", "salesLevel1", "salesLevel2", "salesLevel3"], true),
  route("stats-trends", "/api/v1/stats-trends", "Stats Trends", "stats-trends", "slickQboHis.statsReports", "count"),
  route("sensor-connect", "/api/v1/sensor-connect", "Sensor Connect", "sensor-connect", "slickSiriusKpi.siriusKpiList", "rate", ["region", "kpi"]),
  route("adoption-ap-auto-locate", "/api/v1/adoption/ap-auto-locate", "AP Auto Locate", "adoption", "slickread.apAutoLocate", "rate", ["region", "apModel", "supportState"]),
] as const;

export const SYSTEM_PATHS = [
  "/api/v1/catalog",
  "/api/v1/overview",
  "/api/v1/health",
  "/api/v1/freshness",
  "/api/v1/incidents",
  "/api/v1/openapi.json",
] as const;

export interface Coverage {
  expected: number;
  received: number;
  missing: string[];
}

export interface Provenance {
  sourceKey: string;
  dataset: string;
  relaySchemaVersion?: string;
}

export interface PageMeta {
  limit: number;
  nextCursor: string | null;
  total?: number;
}

export interface ResponseMeta {
  requestId: string;
  generatedAt: string;
  sourceAsOf: string | null;
  freshnessSeconds: number | null;
  status: Status;
  runId: string | null;
  coverage: Coverage;
  provenance: Provenance[];
  page: PageMeta;
}

export interface Envelope<T> {
  schemaVersion: typeof SCHEMA_VERSION;
  data: T;
  meta: ResponseMeta;
}

export interface DomainData {
  domain: MetricDomain;
  rows: ReadonlyArray<Readonly<Record<string, unknown>>>;
  summary: Readonly<Record<string, unknown>> | null;
  facets?: Readonly<Record<string, ReadonlyArray<string>>>;
}

export interface FilterSet {
  from: string | null;
  to: string | null;
  grain: Grain;
  region: string | null;
  accountType: string | null;
  license: string | null;
  solution: string | null;
  networkType: string | null;
  salesLevel1: string | null;
  salesLevel2: string | null;
  salesLevel3: string | null;
  scms: string | null;
  partner: string | null;
  limit: number;
  cursor: string | null;
}

const filterKeyNames = ["region", "accountType", "license", "solution", "networkType", "salesLevel1", "salesLevel2", "salesLevel3", "scms", "partner"] as const;

export type FilterParseResult = { ok: true; value: FilterSet } | { ok: false; error: string };

const isoDate = (value: string | null): string | null => {
  if (!value) return null;
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return null;
  return date.toISOString();
};

export const parseFilters = (params: URLSearchParams, now = new Date("2026-08-18T00:00:00.000Z")): FilterParseResult => {
  const grainValue = params.get("grain") ?? "day";
  if (!["5m", "15m", "hour", "day"].includes(grainValue)) return { ok: false, error: "grain must be one of 5m, 15m, hour, day" };
  const fromRaw = params.get("from");
  const toRaw = params.get("to");
  const from = isoDate(fromRaw);
  const to = isoDate(toRaw);
  if (fromRaw && !from) return { ok: false, error: "from must be an ISO timestamp" };
  if (toRaw && !to) return { ok: false, error: "to must be an ISO timestamp" };
  const fromDate = from ? new Date(from) : null;
  const toDate = to ? new Date(to) : null;
  if (fromDate && toDate && fromDate > toDate) return { ok: false, error: "from must be before to" };
  if (fromDate && toDate && (toDate.getTime() - fromDate.getTime()) > 90 * 86_400_000) return { ok: false, error: "the retained time range is limited to 90 days" };
  if (fromDate && !toDate && (now.getTime() - fromDate.getTime()) > 90 * 86_400_000) return { ok: false, error: "the retained time range is limited to 90 days" };
  const limitRaw = Number(params.get("limit") ?? 50);
  if (!Number.isInteger(limitRaw) || limitRaw < 1 || limitRaw > 250) return { ok: false, error: "limit must be an integer between 1 and 250" };
  const value = {} as FilterSet;
  for (const key of filterKeyNames) value[key] = params.get(key);
  return {
    ok: true,
    value: {
      ...value,
      from,
      to,
      grain: grainValue as Grain,
      limit: limitRaw,
      cursor: params.get("cursor"),
    },
  };
};

const routeByDomain = (domain: MetricDomain): RouteDefinition => {
  const definition = API_ROUTE_DEFINITIONS.find((item) => item.domain === domain);
  if (!definition) throw new Error(`Unknown metric domain: ${domain}`);
  return definition;
};

export const requestId = (): string => {
  const uuid = globalThis.crypto?.randomUUID?.();
  return `req_${uuid ?? `${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`}`;
};

export const emptyDomainEnvelope = (domain: MetricDomain, id = requestId(), now = new Date()): Envelope<DomainData> => {
  const definition = routeByDomain(domain);
  const status: Status = definition.documentationGap ? "DOCUMENTATION_GAP" : "NOT_CONFIGURED";
  const missing = definition.documentationGap
    ? ["HTTPS relay mapping and approved source contract"]
    : ["Cisco API relay", "Cloudflare D1 current snapshot"];
  return {
    schemaVersion: SCHEMA_VERSION,
    data: { domain, rows: [], summary: null },
    meta: {
      requestId: id,
      generatedAt: now.toISOString(),
      sourceAsOf: null,
      freshnessSeconds: null,
      status,
      runId: null,
      coverage: { expected: 1, received: 0, missing },
      provenance: [{ sourceKey: definition.sourceKey, dataset: definition.domain }],
      page: { limit: 50, nextCursor: null, total: 0 },
    },
  };
};

export const envelopeForRows = (
  domain: MetricDomain,
  rows: ReadonlyArray<Readonly<Record<string, unknown>>>,
  options: Partial<Pick<ResponseMeta, "status" | "sourceAsOf" | "freshnessSeconds" | "runId" | "coverage" | "provenance" | "page">> = {},
  id = requestId(),
  now = new Date(),
): Envelope<DomainData> => {
  const definition = routeByDomain(domain);
  const fallback = emptyDomainEnvelope(domain, id, now);
  const status = options.status ?? (definition.documentationGap ? "DOCUMENTATION_GAP" : "HEALTHY");
  return {
    schemaVersion: SCHEMA_VERSION,
    data: { domain, rows, summary: rows.length ? { rowCount: rows.length } : null },
    meta: {
      ...fallback.meta,
      ...options,
      requestId: id,
      generatedAt: now.toISOString(),
      page: options.page ?? { limit: rows.length || 50, nextCursor: null, total: rows.length },
      status,
    },
  };
};

export interface CatalogResponse {
  sourceEnvironment: typeof SOURCE_ENVIRONMENT;
  refreshCadence: { health: string; business: string };
  retention: { currentSnapshot: string; changeLedger: string; aggregates: string };
  routes: readonly RouteDefinition[];
}

export const catalogData = (): CatalogResponse => ({
  sourceEnvironment: SOURCE_ENVIRONMENT,
  refreshCadence: { health: "5 minutes", business: "15 minutes" },
  retention: { currentSnapshot: "latest curated facts", changeLedger: "90 days", aggregates: "90 days" },
  routes: API_ROUTE_DEFINITIONS,
});

export const openApiDocument = (): Record<string, unknown> => {
  const paths: Record<string, unknown> = {};
  for (const definition of API_ROUTE_DEFINITIONS) {
    paths[definition.path] = {
      get: {
        operationId: definition.domain,
        summary: definition.label,
        parameters: [
          { name: "from", in: "query", schema: { type: "string", format: "date-time" } },
          { name: "to", in: "query", schema: { type: "string", format: "date-time" } },
          { name: "grain", in: "query", schema: { type: "string", enum: ["5m", "15m", "hour", "day"] } },
          { name: "region", in: "query", schema: { type: "string" } },
          { name: "limit", in: "query", schema: { type: "integer", minimum: 1, maximum: 250 } },
        ],
        responses: { "200": { description: "Versioned monitoring envelope" }, "400": { description: "Invalid filter" } },
      },
    };
  }
  for (const path of SYSTEM_PATHS) paths[path] = { get: { responses: { "200": { description: "Monitoring system response" } } } };
  return {
    openapi: "3.1.0",
    info: { title: "SLiCK Monitoring API", version: SCHEMA_VERSION, description: "Read-only, source-backed Cisco Spaces monitoring contracts." },
    servers: [{ url: "/" }],
    paths,
  };
};
